/**
 * sectionAseos.tsx — Vista RESUMEN ejecutivo de aseos en /finanzas-empresa.
 *
 * Round 2026-05-13 — rediseño basado en el nuevo flujo financiero del
 * aseo (ver docs/flujo-financiero.md §6.5):
 *
 *   • Los aseos ya NO llevan `rol_de_aloja_facil` ni `pagado_a`. El único
 *     campo de flujo es `pagado_por.tipo` (0=AF, 1=propietario, 2=arrendatario).
 *   • Los destinatarios del dinero son IMPLÍCITOS y FIJOS:
 *       - Aseador asignado SIEMPRE recibe `pago_aseador` (pass-through).
 *       - Lavandería asociada SIEMPRE recibe `lavandería` (subitem,
 *         registrado automáticamente en /lavanderia).
 *       - AF Empresa retiene `transporte + materiales` cuando el pagador
 *         es propietario o arrendatario; egresa los 4 subitems cuando paga
 *         directo de su caja.
 *
 * Cada SectorCard muestra 3 bloques separados por Divider:
 *
 *   1. Brutos: Nº aseos / Σ Costo limpieza / Σ Sueldo aseador.
 *
 *   2. Ingreso AF Empresa (cuando paga propietario o arrendatario):
 *      Σ Transporte + Σ Materiales = Ingreso AF. El pago_aseador y la
 *      lavandería son pass-through (no aportan a AF en este caso).
 *
 *   3. Egreso AF Empresa (cuando AF paga de su caja):
 *      Σ Pago aseador + Σ Transporte + Σ Materiales + Σ Lavandería =
 *      Egreso AF. La parte de lavandería se contrarresta en la pestaña
 *      Lavandería (AF interna: +X; AF externa: ±diferencia vs estimado).
 *
 *   4. Neto AF = Ingreso AF − Egreso AF.
 *
 * Card de "Totales del período" arriba replica la estructura globalmente.
 *
 * Para el detalle granular (filas individuales, filtros, exportación) ir
 * a /aseos. Esta vista es solo lectura ejecutiva.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Row,
  Spin,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowRightOutlined,
  DownOutlined,
  InfoCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { getMsg } from "../../utils/antdStatic";
import { finanzasEmpresaApi } from "../../services/finanzasEmpresa.api";
import { propiedadesApi } from "../../services/propiedades.api";
import { FiltroFecha } from "./filtroGlobalFecha";
import { fetchAllPages } from "./exportXls";
import { ahoraChile } from "../../utils/fechaChile";

dayjs.locale("es");

// ── Types ──────────────────────────────────────────────────────────────────

interface PagoEntidad {
  // tipo 0=AF Empresa, 1=Cliente, 2=Arrendatario (las 3 opciones reales
  // del modelo aseo desde round 2026-05-13). 3/4/5 quedan como compat
  // legacy del JSON pero no se usan en el flujo de aseos.
  tipo?: number | null;
  nombre?: string | null;
  usuario_id?: number | null;
}

export interface AseoRegistro {
  id: string;
  usuarioId?: number | null;
  propiedadId?: number | null;
  // El backend (`cleaningToFrontend` en utils/helpers.js) devuelve solo el
  // NOMBRE del sector como string, no el id. Para el deeplink ?sector=<id>
  // resolvemos el id en el frontend cruzando con el catálogo de sectores
  // cargado desde /api/propiedades/sectores.
  sector?: string | null;
  costoLimpieza?: number | null;
  sueldoAseador?: number | null;
  fechaInicio?: string | null;
  fechaTermino?: string | null;
  horaAseo?: string | null;
  urgencia?: string | null;
  fechaPago?: string | null;
  observaciones?: string | null;
  categoria?: "am" | "pm" | "critico 11:00" | "11:00" | null;
  estado?: "pendiente" | "en_proceso" | "completado" | "pagado" | null;
  // Round 2026-05-13 — único campo de flujo financiero del aseo. Los
  // destinatarios son implícitos (ver doc del archivo arriba).
  pagadoPor?: PagoEntidad | null;
  // 4 subitems financieros del aseo resueltos backend-side desde
  // `cleaning.subitems_override` (override per-aseo) o, en su defecto,
  // desde `property.precio_*_total` / `precio_*`. La regla de flujo se
  // calcula sobre estos campos.
  //   • pagado_por.tipo IN {1, 2}: Ingreso AF = transporte + materiales.
  //   • pagado_por.tipo === 0:     Egreso AF = pago_aseador + transporte +
  //                                 materiales + lavandería.
  // Definición canónica:
  //   ASEO     = pago_aseador + transporte + materiales (sin lavandería).
  //   LIMPIEZA = ASEO + LAVANDERÍA (cobrada al huésped, paquete atómico).
  pagoAseadorSubitem?: number | null;
  transporteSubitem?: number | null;
  materialesSubitem?: number | null;
  lavanderiaSubitem?: number | null;
}

export interface OpcionBasica {
  id: number;
  nombre: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const clp = (v: number | null | undefined) =>
  v != null
    ? new Intl.NumberFormat("es-CL", {
        style: "currency",
        currency: "CLP",
        maximumFractionDigits: 0,
      }).format(v)
    : "—";

// ── Agregado por sector ────────────────────────────────────────────────────
//
// Tomamos el array completo de aseos del período y lo agrupamos por
// id_sector. Las filas sin sector caen en un grupo especial "Sin sector"
// (clave `__sin_sector__`).

interface SectorAggregate {
  // sectorId queda como info útil (resolvemos contra el catálogo) pero el
  // deeplink usa `sectorNombre` porque el filtro del AseoFilterBar se
  // construye con `value: sector.nombre` (no id).
  sectorId: number | null;
  sectorNombre: string;
  // true cuando el aseo no tenía sector asociado (cae en grupo "Sin sector").
  esSinSector: boolean;
  count: number;
  totalCostoLimpieza: number;
  totalSueldoAseador: number;

  // ── Ingreso AF Empresa (cuando paga propietario o arrendatario) ─────
  // Suma de transporte y materiales SOLO cuando `pagado_por.tipo` es 1
  // (propietario) o 2 (arrendatario). El pago_aseador y la lavandería son
  // pass-through al aseador / lavandería y NO aportan a la caja AF.
  ingresoAfTransporte: number;
  ingresoAfMateriales: number;
  ingresoAfTotal: number; // = ingresoAfTransporte + ingresoAfMateriales
  countPagoExterno: number; // cantidad de aseos donde paga prop/arr

  // ── Egreso AF Empresa (cuando AF paga de su caja) ──────────────────
  // Suma de los 4 subitems SOLO cuando `pagado_por.tipo === 0`.
  // Importante: la lavandería se contrarresta en /finanzas-empresa →
  // pestaña Lavandería (AF interna: +X ingreso lavandería; AF externa:
  // ±diferencia vs estimado).
  egresoAfPagoAseador: number;
  egresoAfTransporte: number;
  egresoAfMateriales: number;
  egresoAfLavanderia: number;
  egresoAfTotal: number; // = sum de los 4 subitems
  countPagoAf: number; // cantidad de aseos donde paga AF

  // Neto = Ingreso − Egreso
  netoAf: number;

  porEstado: Record<string, { count: number; monto: number }>;
  porUrgencia: Record<string, number>;
  aseos: AseoRegistro[];
}

const SIN_SECTOR_KEY = "__sin_sector__";

function aggregateBySector(
  rows: AseoRegistro[],
  sectoresCatalogo: OpcionBasica[],
): SectorAggregate[] {
  const map = new Map<string, SectorAggregate>();
  // Backend devuelve solo `sector` (nombre). Construimos el lookup
  // inverso nombre→id desde el catálogo para resolver el deeplink.
  // Si un aseo tiene un nombre de sector que no existe en el catálogo
  // (caso raro: sector renombrado o borrado después de crear el aseo),
  // cae en el grupo del nombre con sectorId=null y el botón "Ver detalle"
  // abre /aseos sin filtro pre-aplicado.
  const sectorIdByNombre = new Map(
    sectoresCatalogo.map((s) => [s.nombre, s.id]),
  );

  for (const r of rows) {
    const nombre = r.sector ?? null;
    const sid = nombre ? sectorIdByNombre.get(nombre) ?? null : null;
    const key = nombre ?? SIN_SECTOR_KEY;
    let agg = map.get(key);
    if (!agg) {
      agg = {
        sectorId: sid,
        sectorNombre: nombre ?? "Sin sector",
        esSinSector: nombre == null,
        count: 0,
        totalCostoLimpieza: 0,
        totalSueldoAseador: 0,
        ingresoAfTransporte: 0,
        ingresoAfMateriales: 0,
        ingresoAfTotal: 0,
        countPagoExterno: 0,
        egresoAfPagoAseador: 0,
        egresoAfTransporte: 0,
        egresoAfMateriales: 0,
        egresoAfLavanderia: 0,
        egresoAfTotal: 0,
        countPagoAf: 0,
        netoAf: 0,
        porEstado: {},
        porUrgencia: {},
        aseos: [],
      };
      map.set(key, agg);
    }
    agg.count += 1;
    agg.totalCostoLimpieza += Number(r.costoLimpieza) || 0;
    agg.totalSueldoAseador += Number(r.sueldoAseador) || 0;

    // Subitems pre-resueltos del backend.
    const pagoAseador = Number(r.pagoAseadorSubitem) || 0;
    const transporte = Number(r.transporteSubitem) || 0;
    const materiales = Number(r.materialesSubitem) || 0;
    const lavanderia = Number(r.lavanderiaSubitem) || 0;

    // Regla del flujo (canónica única en utils/aseoFinance.ts → flujoAseoAF,
    // §6.5). Acá se desglosa por subítem SOLO para el display (Σ Transporte,
    // Σ Materiales, etc.); el ingreso/egreso total resultante DEBE coincidir
    // con flujoAseoAF. Tipo del pagador:
    //   0 = AF Empresa    → AF egresa los 4 subitems.
    //   1 = Cliente       → AF ingresa transporte + materiales.
    //   2 = Arrendatario  → AF ingresa transporte + materiales.
    const pagadorTipo = r.pagadoPor?.tipo ?? null;
    if (pagadorTipo === 0) {
      agg.egresoAfPagoAseador += pagoAseador;
      agg.egresoAfTransporte += transporte;
      agg.egresoAfMateriales += materiales;
      agg.egresoAfLavanderia += lavanderia;
      agg.egresoAfTotal += pagoAseador + transporte + materiales + lavanderia;
      agg.countPagoAf += 1;
    } else if (pagadorTipo === 1 || pagadorTipo === 2) {
      agg.ingresoAfTransporte += transporte;
      agg.ingresoAfMateriales += materiales;
      agg.ingresoAfTotal += transporte + materiales;
      agg.countPagoExterno += 1;
    }

    const estado = r.estado ?? "sin_estado";
    if (!agg.porEstado[estado]) {
      agg.porEstado[estado] = { count: 0, monto: 0 };
    }
    agg.porEstado[estado].count += 1;
    agg.porEstado[estado].monto += Number(r.sueldoAseador) || 0;

    const urgencia = r.urgencia ?? "sin_urgencia";
    agg.porUrgencia[urgencia] = (agg.porUrgencia[urgencia] || 0) + 1;

    agg.aseos.push(r);
  }

  // Calcular neto AF para cada sector (después de acumular).
  for (const agg of map.values()) {
    agg.netoAf = agg.ingresoAfTotal - agg.egresoAfTotal;
  }

  // Orden: alfabético por nombre, con "Sin sector" al final si existe.
  const result = Array.from(map.values());
  result.sort((a, b) => {
    if (a.esSinSector && !b.esSinSector) return 1;
    if (!a.esSinSector && b.esSinSector) return -1;
    return a.sectorNombre.localeCompare(b.sectorNombre);
  });
  return result;
}

// ── Etiquetas humanas para los códigos de estado/urgencia ──────────────────

const ESTADO_LABELS: Record<string, { label: string; color: string }> = {
  pendiente: { label: "Pendiente", color: "orange" },
  en_proceso: { label: "En proceso", color: "blue" },
  completado: { label: "Completado", color: "cyan" },
  pagado: { label: "Pagado", color: "green" },
  sin_estado: { label: "Sin estado", color: "default" },
};

const URGENCIA_LABELS: Record<string, string> = {
  am: "AM",
  pm: "PM",
  "11:00": "11:00",
  "critico 11:00": "Crítico 11:00",
  sin_urgencia: "Sin urgencia",
};

// ── Sub-componente: fila de subitem dentro de un bloque ────────────────────
//
// Renderiza una línea con etiqueta (con tooltip), monto en CLP y color
// distintivo. Se usa para enumerar Transporte / Materiales / Pago aseador /
// Lavandería en los bloques de Ingreso y Egreso AF.

function SubitemRow({
  label,
  tooltip,
  value,
  color,
  icon,
}: {
  label: string;
  tooltip: string;
  value: number;
  color: string;
  icon: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "6px 0",
        borderBottom: "1px dashed #eee",
      }}
    >
      <Tooltip title={tooltip}>
        <span style={{ fontSize: 13, color, fontWeight: 500 }}>
          {icon} {label} <InfoCircleOutlined style={{ fontSize: 11 }} />
        </span>
      </Tooltip>
      <span style={{ fontSize: 14, fontWeight: 600, color }}>{clp(value)}</span>
    </div>
  );
}

// ── Sub-componente: 2 columnas (Ingreso ↔ Egreso) con sus subtotales ──────
//
// Round 2026-05-13 iter B — compresión vertical de las cards. Antes los
// bloques de Ingreso AF y Egreso AF se renderizaban apilados (uno debajo
// del otro), ocupando ~el doble de altura. Ahora viven lado a lado en md+
// (xs={24} sm={24} md={12} → apilan en mobile, lado a lado en desktop).
// El Neto AF queda en una fila independiente abajo, como cierre.

interface IngresoEgresoColumnsProps {
  // Counts y montos de cada lado.
  countPagoExterno: number;
  ingresoAfTransporte: number;
  ingresoAfMateriales: number;
  ingresoAfTotal: number;

  countPagoAf: number;
  egresoAfPagoAseador: number;
  egresoAfTransporte: number;
  egresoAfMateriales: number;
  egresoAfLavanderia: number;
  egresoAfTotal: number;

  // Sufijo del label (sector o "global") para el Alert info.
  alertScopeLabel: string; // ej "del sector", "del período"
  subitemSuffix?: string;  // ej "" o " (global)" para los labels Σ
}

function IngresoEgresoColumns({
  countPagoExterno,
  ingresoAfTransporte,
  ingresoAfMateriales,
  ingresoAfTotal,
  countPagoAf,
  egresoAfPagoAseador,
  egresoAfTransporte,
  egresoAfMateriales,
  egresoAfLavanderia,
  egresoAfTotal,
  alertScopeLabel,
  subitemSuffix = "",
}: IngresoEgresoColumnsProps) {
  return (
    <Row gutter={[16, 16]}>
      {/* ── Columna izquierda: INGRESO AF ─────────────────────────────── */}
      <Col xs={24} md={12}>
        <div
          style={{
            padding: "12px 16px",
            background: "#fafff5",
            border: "1px solid #d9f7be",
            borderRadius: 6,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "#389e0d",
              fontWeight: 600,
              marginBottom: 8,
              textAlign: "center",
            }}
          >
            💵 Ingreso AF — paga propietario o arrendatario
          </div>
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 8, padding: "4px 8px" }}
            message={
              <span style={{ fontSize: 11 }}>
                <strong>
                  {countPagoExterno} aseo{countPagoExterno === 1 ? "" : "s"}
                </strong>{" "}
                {alertScopeLabel}. AF retiene transporte + materiales (el pago
                aseador y la lavandería son pass-through).
              </span>
            }
          />
          <div style={{ flex: 1 }}>
            <SubitemRow
              label={`Σ Transporte${subitemSuffix}`}
              tooltip="Subitem cobrado al huésped por el traslado del aseador. AF retiene este monto cuando paga propietario o arrendatario."
              value={ingresoAfTransporte}
              color="#7c3aed"
              icon="🚗"
            />
            <SubitemRow
              label={`Σ Materiales${subitemSuffix}`}
              tooltip="Subitem cobrado al huésped por insumos de limpieza. AF retiene este monto cuando paga propietario o arrendatario."
              value={ingresoAfMateriales}
              color="#fa8c16"
              icon="🧴"
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0 2px",
              borderTop: "2px solid #389e0d",
              marginTop: 6,
            }}
          >
            <Tooltip title="Ingreso AF = Σ Transporte + Σ Materiales (solo aseos pagados por propietario o arrendatario).">
              <span style={{ fontSize: 13, color: "#389e0d", fontWeight: 700 }}>
                💵 Subtotal <InfoCircleOutlined style={{ fontSize: 11 }} />
              </span>
            </Tooltip>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#389e0d" }}>
              {clp(ingresoAfTotal)}
            </span>
          </div>
        </div>
      </Col>

      {/* ── Columna derecha: EGRESO AF ─────────────────────────────────── */}
      <Col xs={24} md={12}>
        <div
          style={{
            padding: "12px 16px",
            background: "#fff7f6",
            border: "1px solid #ffccc7",
            borderRadius: 6,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "#cf1322",
              fontWeight: 600,
              marginBottom: 8,
              textAlign: "center",
            }}
          >
            💳 Egreso AF — paga AF directo
          </div>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8, padding: "4px 8px" }}
            message={
              <span style={{ fontSize: 11 }}>
                <strong>
                  {countPagoAf} aseo{countPagoAf === 1 ? "" : "s"}
                </strong>{" "}
                {alertScopeLabel} pagados por AF. La lavandería se contrarresta
                en la pestaña <em>Lavandería</em>.
              </span>
            }
          />
          <div style={{ flex: 1 }}>
            <SubitemRow
              label={`Σ Pago aseador${subitemSuffix}`}
              tooltip="Lo que AF egresa al aseador cuando AF paga el aseo (sale de la caja AF, no es pass-through)."
              value={egresoAfPagoAseador}
              color="#1677ff"
              icon="🧑‍🔧"
            />
            <SubitemRow
              label={`Σ Transporte${subitemSuffix}`}
              tooltip="Subitem de transporte que AF cubre cuando paga directo."
              value={egresoAfTransporte}
              color="#7c3aed"
              icon="🚗"
            />
            <SubitemRow
              label={`Σ Materiales${subitemSuffix}`}
              tooltip="Subitem de materiales que AF cubre cuando paga directo."
              value={egresoAfMateriales}
              color="#fa8c16"
              icon="🧴"
            />
            <SubitemRow
              label={`Σ Lavandería${subitemSuffix}`}
              tooltip="Subitem de lavandería que AF paga a la lavandería asociada. Se contrarresta en /finanzas-empresa → pestaña Lavandería."
              value={egresoAfLavanderia}
              color="#13c2c2"
              icon="🧺"
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0 2px",
              borderTop: "2px solid #cf1322",
              marginTop: 6,
            }}
          >
            <Tooltip title="Egreso AF = Σ Pago aseador + Σ Transporte + Σ Materiales + Σ Lavandería (solo aseos pagados por AF directo).">
              <span style={{ fontSize: 13, color: "#cf1322", fontWeight: 700 }}>
                💳 Subtotal <InfoCircleOutlined style={{ fontSize: 11 }} />
              </span>
            </Tooltip>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#cf1322" }}>
              {clp(egresoAfTotal)}
            </span>
          </div>
        </div>
      </Col>
    </Row>
  );
}

// ── Sub-componente: una Card por sector ────────────────────────────────────

interface SectorCardProps {
  agg: SectorAggregate;
  // Rango del período activo (mismo que filtra este resumen) para que el
  // deep-link a /aseos muestre EXACTAMENTE los mismos aseos. Ambas vistas
  // filtran por el MISMO campo (fecha_inicio del aseo). Round 2026-07-14.
  desde?: string;
  hasta?: string;
}

function SectorCard({ agg, desde, hasta }: SectorCardProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const verDetalleEnAseos = () => {
    // Deep-link a /aseos correlacionado con lo que muestra esta card:
    //   • `sector=<nombre>` → pre-aplica el filtro de sector (el filtro de
    //     /aseos usa el NOMBRE, no el id — ver aseos.tsx sectorSelectOptions).
    //   • `desde`/`hasta`   → pre-aplica el MISMO rango de fechas del período.
    //     Sin esto /aseos abría el sector pero SIN período → listaba todos los
    //     aseos del sector en toda la historia (no correlacionaba con el mes
    //     mostrado). Ambas vistas filtran por fecha_inicio, así que el rango
    //     calza 1:1. Round 2026-07-14.
    const params = new URLSearchParams();
    if (!agg.esSinSector && agg.sectorNombre) {
      params.set("sector", agg.sectorNombre);
    }
    if (desde) params.set("desde", desde);
    if (hasta) params.set("hasta", hasta);
    const qs = params.toString();
    navigate(qs ? `/aseos?${qs}` : "/aseos");
  };

  return (
    <Card
      style={{ marginBottom: 16 }}
      bodyStyle={{ padding: "20px 24px" }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tag color="purple" style={{ fontSize: 13, marginRight: 0 }}>
            🧹 {agg.sectorNombre}
          </Tag>
          <span style={{ fontSize: 12, color: "#6b7280" }}>
            {agg.count} {agg.count === 1 ? "aseo" : "aseos"} en el período
          </span>
        </div>
      }
    >
      {/* ── Bloque 1: BRUTOS ───────────────────────────────────────── */}
      <Row gutter={[24, 16]} align="middle">
        <Col xs={24} sm={8}>
          <Statistic
            title={
              <Tooltip title="Cantidad total de aseos del sector en el período seleccionado (todos los estados).">
                <span style={{ fontSize: 13, color: "#7c3aed", fontWeight: 600 }}>
                  Nº de aseos <InfoCircleOutlined />
                </span>
              </Tooltip>
            }
            value={agg.count}
            valueStyle={{ color: "#7c3aed", fontSize: 22 }}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Statistic
            title={
              <Tooltip
                title={
                  "Σ Costo limpieza acumulado en el sector (todos los aseos, " +
                  "todos los pagadores). Es el monto BRUTO cobrado al cliente " +
                  "final por la limpieza (incluye los 4 subitems: pago aseador, " +
                  "transporte, materiales, lavandería)."
                }
              >
                <span style={{ fontSize: 13, color: "#389e0d", fontWeight: 600 }}>
                  Σ Costo limpieza (bruto) <InfoCircleOutlined />
                </span>
              </Tooltip>
            }
            value={agg.totalCostoLimpieza}
            precision={0}
            formatter={(v) => clp(Number(v))}
            valueStyle={{ color: "#389e0d", fontSize: 22 }}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Statistic
            title={
              <Tooltip
                title={
                  "Σ Sueldo aseador acumulado en el sector (todos los aseos, " +
                  "incluyendo pendientes de liquidar). El sueldo se paga al " +
                  "aseador asignado, sin importar quién paga el aseo: cuando " +
                  "paga AF, AF lo desembolsa de su caja; cuando paga propietario " +
                  "o arrendatario, AF lo cobra y lo transfiere íntegro (pass-through)."
                }
              >
                <span style={{ fontSize: 13, color: "#1677ff", fontWeight: 600 }}>
                  Σ Sueldo aseador (bruto) <InfoCircleOutlined />
                </span>
              </Tooltip>
            }
            value={agg.totalSueldoAseador}
            precision={0}
            formatter={(v) => clp(Number(v))}
            valueStyle={{ color: "#1677ff", fontSize: 22 }}
          />
        </Col>
      </Row>

      {/* ── Bloques 2 + 3: INGRESO AF (izq) ↔ EGRESO AF (der) en columnas ── */}
      <Divider style={{ margin: "16px 0 12px" }}>
        <span style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>
          Flujo AF Empresa del sector
        </span>
      </Divider>
      <IngresoEgresoColumns
        countPagoExterno={agg.countPagoExterno}
        ingresoAfTransporte={agg.ingresoAfTransporte}
        ingresoAfMateriales={agg.ingresoAfMateriales}
        ingresoAfTotal={agg.ingresoAfTotal}
        countPagoAf={agg.countPagoAf}
        egresoAfPagoAseador={agg.egresoAfPagoAseador}
        egresoAfTransporte={agg.egresoAfTransporte}
        egresoAfMateriales={agg.egresoAfMateriales}
        egresoAfLavanderia={agg.egresoAfLavanderia}
        egresoAfTotal={agg.egresoAfTotal}
        alertScopeLabel="del sector"
      />

      {/* ── Bloque 4: NETO AF (fila independiente al final) ───────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 16px",
          marginTop: 12,
          background: agg.netoAf >= 0 ? "#f6ffed" : "#fff1f0",
          borderRadius: 6,
          border: `2px solid ${agg.netoAf >= 0 ? "#389e0d" : "#cf1322"}`,
        }}
      >
        <Tooltip
          title={
            "Neto AF del sector = Ingreso AF − Egreso AF. Positivo: AF retiene " +
            "más de lo que egresa por aseos. Negativo: AF cubre más de lo que " +
            "ingresa (típicamente porque cubre aseos de su caja sin cobrarlos)."
          }
        >
          <span style={{ fontSize: 14, fontWeight: 700 }}>
            📊 Neto AF del sector <InfoCircleOutlined />
          </span>
        </Tooltip>
        <span
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: agg.netoAf >= 0 ? "#389e0d" : "#cf1322",
          }}
        >
          {agg.netoAf >= 0 ? "+" : ""}
          {clp(agg.netoAf)}
        </span>
      </div>

      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Button
          type="link"
          onClick={() => setExpanded((s) => !s)}
          icon={expanded ? <DownOutlined /> : <RightOutlined />}
        >
          {expanded ? "Ocultar detalle" : "Ver más detalle"}
        </Button>
      </div>

      {expanded && (
        <div
          style={{
            marginTop: 12,
            padding: 16,
            background: "#fafafa",
            borderRadius: 6,
          }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Typography.Title level={5} style={{ marginTop: 0 }}>
                Distribución por estado de pago
              </Typography.Title>
              {Object.entries(agg.porEstado)
                .sort((a, b) => b[1].count - a[1].count)
                .map(([estado, info]) => {
                  const meta =
                    ESTADO_LABELS[estado] ?? {
                      label: estado,
                      color: "default",
                    };
                  return (
                    <div
                      key={estado}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "6px 0",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <Tag color={meta.color}>{meta.label}</Tag>
                      <span style={{ fontSize: 13 }}>
                        <b>{info.count}</b>
                        {info.count === 1 ? " aseo" : " aseos"} ·{" "}
                        <span style={{ color: "#888" }}>{clp(info.monto)}</span>
                      </span>
                    </div>
                  );
                })}
            </Col>

            <Col xs={24} md={12}>
              <Typography.Title level={5} style={{ marginTop: 0 }}>
                Distribución por urgencia
              </Typography.Title>
              {Object.entries(agg.porUrgencia)
                .sort((a, b) => b[1] - a[1])
                .map(([urgencia, count]) => {
                  const label = URGENCIA_LABELS[urgencia] ?? urgencia;
                  return (
                    <div
                      key={urgencia}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "6px 0",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <Tag>{label}</Tag>
                      <span style={{ fontSize: 13 }}>
                        <b>{count}</b>
                        {count === 1 ? " aseo" : " aseos"}
                      </span>
                    </div>
                  );
                })}
            </Col>
          </Row>

          <div
            style={{
              marginTop: 24,
              paddingTop: 16,
              borderTop: "1px solid #e8e8e8",
              textAlign: "center",
            }}
          >
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={verDetalleEnAseos}
            >
              Ver detalle del sector en /aseos
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export function AseosSection({
  filtroFecha,
  refreshKey,
}: {
  // `data`, `loading`, `usuarios` y `propiedades` quedan en la signature
  // por compat con el padre pero ya no se usan acá — el resumen carga
  // su propia data desde el endpoint paginado y agrupa client-side.
  data?: AseoRegistro[];
  loading?: boolean;
  usuarios?: OpcionBasica[];
  propiedades?: OpcionBasica[];
  filtroFecha?: FiltroFecha;
  /** Contador de refresco manual (botón "Actualizar" del header). */
  refreshKey?: number;
}) {
  const navigate = useNavigate();
  const [allRows, setAllRows] = useState<AseoRegistro[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [sectores, setSectores] = useState<OpcionBasica[]>([]);

  const buildGlobalRange = useCallback(() => {
    const ff = filtroFecha ?? { modo: "mes", mes: ahoraChile() };
    if (ff.modo === "mes" || ff.modo === "anterior") {
      const m = ff.mes ?? ahoraChile();
      return { from: m.startOf("month"), to: m.endOf("month") };
    }
    if (ff.modo === "rango" && ff.rango) {
      return { from: ff.rango[0].startOf("day"), to: ff.rango[1].endOf("day") };
    }
    const m = ahoraChile();
    return { from: m.startOf("month"), to: m.endOf("month") };
  }, [filtroFecha]);

  // Rango del período activo en el formato de los deep-links a /aseos. Ambas
  // vistas filtran por el MISMO campo (fecha_inicio del aseo), así que pasando
  // este rango /aseos lista exactamente los mismos aseos que este resumen.
  const deepLinkRange = useMemo(() => {
    const gr = buildGlobalRange();
    return {
      desde: gr.from.format("YYYY-MM-DD"),
      hasta: gr.to.format("YYYY-MM-DD"),
    };
  }, [buildGlobalRange]);

  const fetchAseos = useCallback(async () => {
    try {
      setLoadingRows(true);
      const gr = buildGlobalRange();
      const globalDateRange = [
        gr.from.format("YYYY-MM-DD"),
        gr.to.format("YYYY-MM-DD"),
      ];
      // Para el resumen por sector necesitamos TODAS las filas del
      // período, no solo la primera página de 20. Usamos `fetchAllPages`
      // (el mismo helper que usa la exportación XLS) para iterar.
      // Acepta volumen alto porque el backend permite page_size grande.
      const rows = await fetchAllPages<AseoRegistro>(async (page, pageSize) => {
        const body = {
          filters: { fields: {} },
          globalDateRange,
          page,
          page_size: pageSize,
        };
        const json = await finanzasEmpresaApi.searchAseos(body);
        if (!json) return null;
        return { data: json.data, total: json.total };
      });
      setAllRows(rows);
    } catch (err: any) {
      getMsg()?.error(err?.message || "Error al cargar aseos");
    } finally {
      setLoadingRows(false);
    }
  }, [buildGlobalRange]);

  const fetchSectores = useCallback(async () => {
    const data = await propiedadesApi.getSectores();
    if (data?.data) {
      setSectores(data.data.map((s: any) => ({ id: s.id, nombre: s.sector })));
    }
  }, []);

  useEffect(() => {
    fetchAseos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAseos, refreshKey]);

  useEffect(() => {
    fetchSectores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSectores, refreshKey]);

  const aggregates = useMemo(
    () => aggregateBySector(allRows, sectores),
    [allRows, sectores],
  );

  // Totales globales del período (suma de todas las cards).
  const totales = useMemo(() => {
    return aggregates.reduce(
      (acc, a) => {
        acc.count += a.count;
        acc.countPagoExterno += a.countPagoExterno;
        acc.countPagoAf += a.countPagoAf;
        acc.costoLimpieza += a.totalCostoLimpieza;
        acc.sueldoAseador += a.totalSueldoAseador;
        acc.ingresoAfTransporte += a.ingresoAfTransporte;
        acc.ingresoAfMateriales += a.ingresoAfMateriales;
        acc.ingresoAfTotal += a.ingresoAfTotal;
        acc.egresoAfPagoAseador += a.egresoAfPagoAseador;
        acc.egresoAfTransporte += a.egresoAfTransporte;
        acc.egresoAfMateriales += a.egresoAfMateriales;
        acc.egresoAfLavanderia += a.egresoAfLavanderia;
        acc.egresoAfTotal += a.egresoAfTotal;
        return acc;
      },
      {
        count: 0,
        countPagoExterno: 0,
        countPagoAf: 0,
        costoLimpieza: 0,
        sueldoAseador: 0,
        ingresoAfTransporte: 0,
        ingresoAfMateriales: 0,
        ingresoAfTotal: 0,
        egresoAfPagoAseador: 0,
        egresoAfTransporte: 0,
        egresoAfMateriales: 0,
        egresoAfLavanderia: 0,
        egresoAfTotal: 0,
      },
    );
  }, [aggregates]);
  const netoAfTotal = totales.ingresoAfTotal - totales.egresoAfTotal;

  // Etiqueta del rango activo.
  const rangoActivo = useMemo(() => {
    const ff = filtroFecha;
    if (!ff || ff.modo === "mes") {
      const m = ff?.mes ?? ahoraChile();
      return m.format("MMMM YYYY");
    }
    if (ff.modo === "anterior") {
      const m = ff.mes ?? ahoraChile().subtract(1, "month");
      return `${m.format("MMMM YYYY")} (mes anterior)`;
    }
    if (ff.modo === "rango" && ff.rango) {
      return `${ff.rango[0].format("DD-MM-YYYY")} → ${ff.rango[1].format("DD-MM-YYYY")}`;
    }
    return "Mes actual";
  }, [filtroFecha]);

  return (
    <div className="finanzas-section">
      {/* Indicador del rango activo. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
          padding: "8px 12px",
          background: "#f3e8ff",
          borderRadius: 6,
        }}
      >
        <span style={{ fontWeight: 600, color: "#7c3aed" }}>📅 Período:</span>
        <Tag color="purple" style={{ fontSize: 13, textTransform: "capitalize" }}>
          {rangoActivo}
        </Tag>
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          (cambia el rango en el selector global de arriba)
        </span>
      </div>

      {/* ── Card de Totales del período (todos los sectores) ─────────── */}
      {!loadingRows && totales.count > 0 && (
        <Card
          style={{
            marginBottom: 16,
            background: "linear-gradient(135deg, #f3e8ff 0%, #fafafa 100%)",
          }}
          bodyStyle={{ padding: "20px 24px" }}
        >
          <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>
            📊 Totales del período (todos los sectores)
          </Typography.Title>

          {/* Brutos globales */}
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={8}>
              <Statistic
                title={
                  <Tooltip title="Cantidad total de aseos del período en todos los sectores.">
                    <span style={{ fontSize: 13, color: "#7c3aed", fontWeight: 600 }}>
                      Nº total de aseos <InfoCircleOutlined />
                    </span>
                  </Tooltip>
                }
                value={totales.count}
                valueStyle={{ color: "#7c3aed", fontSize: 24, fontWeight: 700 }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title={
                  <Tooltip title="Σ Costo limpieza global — monto bruto cobrado al cliente final por la limpieza (incluye los 4 subitems).">
                    <span style={{ fontSize: 13, color: "#389e0d", fontWeight: 600 }}>
                      Σ Costo limpieza <InfoCircleOutlined />
                    </span>
                  </Tooltip>
                }
                value={totales.costoLimpieza}
                precision={0}
                formatter={(v) => clp(Number(v))}
                valueStyle={{ color: "#389e0d", fontSize: 24, fontWeight: 700 }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title={
                  <Tooltip title="Σ Sueldo aseador global — lo que se transfiere íntegro a los aseadores asignados.">
                    <span style={{ fontSize: 13, color: "#1677ff", fontWeight: 600 }}>
                      Σ Sueldo aseador <InfoCircleOutlined />
                    </span>
                  </Tooltip>
                }
                value={totales.sueldoAseador}
                precision={0}
                formatter={(v) => clp(Number(v))}
                valueStyle={{ color: "#1677ff", fontSize: 24, fontWeight: 700 }}
              />
            </Col>
          </Row>

          {/* ── Ingreso AF (izq) ↔ Egreso AF (der) en columnas — global ── */}
          <Divider style={{ margin: "16px 0 12px" }}>
            <span style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>
              Flujo AF Empresa del período
            </span>
          </Divider>
          <IngresoEgresoColumns
            countPagoExterno={totales.countPagoExterno}
            ingresoAfTransporte={totales.ingresoAfTransporte}
            ingresoAfMateriales={totales.ingresoAfMateriales}
            ingresoAfTotal={totales.ingresoAfTotal}
            countPagoAf={totales.countPagoAf}
            egresoAfPagoAseador={totales.egresoAfPagoAseador}
            egresoAfTransporte={totales.egresoAfTransporte}
            egresoAfMateriales={totales.egresoAfMateriales}
            egresoAfLavanderia={totales.egresoAfLavanderia}
            egresoAfTotal={totales.egresoAfTotal}
            alertScopeLabel="del período"
            subitemSuffix=" (global)"
          />

          {/* ── Neto AF total del período (fila independiente al final) ── */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 18px",
              marginTop: 12,
              background: netoAfTotal >= 0 ? "#f6ffed" : "#fff1f0",
              borderRadius: 8,
              border: `2px solid ${netoAfTotal >= 0 ? "#389e0d" : "#cf1322"}`,
            }}
          >
            <Tooltip title="Neto AF total del período = Σ Ingreso AF − Σ Egreso AF (todos los sectores).">
              <span style={{ fontSize: 15, fontWeight: 700 }}>
                📊 Neto AF total del período <InfoCircleOutlined />
              </span>
            </Tooltip>
            <span
              style={{
                fontSize: 24,
                fontWeight: 700,
                color: netoAfTotal >= 0 ? "#389e0d" : "#cf1322",
              }}
            >
              {netoAfTotal >= 0 ? "+" : ""}
              {clp(netoAfTotal)}
            </span>
          </div>

          {/* Ver TODOS los aseos del período en /aseos (sin filtro de sector,
              todas las ubicaciones), correlacionado por rango de fechas.
              Equivalente global del "Ver detalle del sector" de cada card.
              Round 2026-07-15. */}
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={() => {
                const params = new URLSearchParams();
                if (deepLinkRange.desde) params.set("desde", deepLinkRange.desde);
                if (deepLinkRange.hasta) params.set("hasta", deepLinkRange.hasta);
                navigate(`/aseos?${params.toString()}`);
              }}
            >
              Ver todos los aseos del período en /aseos
            </Button>
          </div>
        </Card>
      )}

      {/* Banner explicativo del flujo AF de aseos. Aclara EXPLÍCITAMENTE el
          pass-through: el pago al aseador y la lavandería NO son ingreso de AF
          (pedido del owner 2026-07-14). Va debajo de la card de Totales para
          que ésta sea lo primero del período. */}
      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 16 }}
        message="Cómo se lee el flujo AF de aseos (importante)"
        description={
          <span>
            Cada Card muestra <b>3 bloques</b>: (1) los montos <b>brutos</b> del
            sector, (2) el <b>Ingreso AF</b> cuando paga el propietario o el
            arrendatario, y (3) el <b>Egreso AF</b> cuando AF paga de su caja.
            El <b>Neto AF</b> = Ingreso − Egreso.
            <br />
            <b>
              ⚠️ El pago al aseador y la lavandería NO se cuentan como ingreso
              de AF.
            </b>{" "}
            Cuando paga un externo (propietario o arrendatario), el huésped paga
            la limpieza completa a AF, pero AF transfiere ese dinero{" "}
            <b>íntegro</b> al aseador y a la lavandería (entra y sale por el
            mismo monto → es <b>pass-through</b>, no es ganancia). Por eso el{" "}
            <b>Ingreso AF = solo Σ Transporte + Σ Materiales</b> (lo único que
            AF retiene).
            <br />
            Solo cuando <b>paga AF directo</b> los 4 subítems son <b>Egreso AF</b>{" "}
            (pago aseador + transporte + materiales + lavandería), porque el
            dinero sale de la caja de AF. La lavandería se reconcilia (cobro real
            vs estimado) en su propia pestaña. Detalle por fila en{" "}
            <Typography.Link
              onClick={() => {
                const params = new URLSearchParams();
                if (deepLinkRange.desde) params.set("desde", deepLinkRange.desde);
                if (deepLinkRange.hasta) params.set("hasta", deepLinkRange.hasta);
                navigate(`/aseos?${params.toString()}`);
              }}
            >
              /aseos
            </Typography.Link>
            .
          </span>
        }
      />

      {loadingRows ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
          <div style={{ marginTop: 12, color: "#888" }}>
            Cargando aseos del período...
          </div>
        </div>
      ) : aggregates.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="Sin aseos en el período"
          description="No hay aseos registrados en el rango seleccionado. Cambia el período en el selector global de arriba o ir a /aseos para crear uno nuevo."
        />
      ) : (
        aggregates.map((agg) => (
          <SectorCard
            // Key ESTABLE y única por nombre de sector (aggregateBySector
            // agrupa por nombre → cada nombre aparece una sola vez). Antes
            // usaba `sectorId`, que es null mientras el catálogo de sectores
            // carga async: en ese lapso TODAS las cards compartían la misma
            // key (SIN_SECTOR_KEY) y el remount posterior dejaba el `expanded`
            // de alguna card sin responder → "Ver más detalle" no abría en esa
            // card. Con la key por nombre no depende de esa carga. Round 2026-07-14.
            key={agg.esSinSector ? SIN_SECTOR_KEY : `sector-${agg.sectorNombre}`}
            agg={agg}
            desde={deepLinkRange.desde}
            hasta={deepLinkRange.hasta}
          />
        ))
      )}
    </div>
  );
}
